import React, { useMemo, useRef, useCallback, useState } from 'react';
import { Box, Form, IconButton, Spec, Typography, Loader } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { BusinessNameSearch } from '../../../../../../components/BusinessNameSearch/BusinessNameSearch';
import type {
  BusinessSearchResult,
  ModuleFileReference,
} from '../../../../../../api/services/lupp/order/dto.order';
import { createFinanceProviderFormFields } from './formFields';
import type { FinanceProviderDetailsData } from '../types';
import { FileHubApi } from '../../../../../../api/services/lupp/lupp.service';
import { getFilesByCategory } from '../../../../../../api/services/lupp/order/transformers.order';
import { fetchSfdcFileBlob } from '../../../../../../api/services/lupp/fileUpload/fileUpload';
import { DOCUMENT_CATEGORY, FILEHUB_MODULE_NAME } from '../../../../../../constants/constants';

type FinancingAgreementFieldProps = {
  label: string;
  hasAgreement: boolean;
  onDownload?: () => void;
  downloadErrorMessage?: string | null;
  theme?: 'light' | 'dark';
};

const FinancingAgreementField: React.FC<FinancingAgreementFieldProps> = ({
  label,
  hasAgreement,
  onDownload,
  downloadErrorMessage,
  theme = 'light',
}) => {
  return (
    <Box>
      <Spec variant="gray" theme="light" size="extraSmall" type="sans" eyebrow={label} />
      {hasAgreement ? (
        <IconButton
          buttonType="tertiary"
          color="neutral"
          dataTestId="download-financing-agreement-button"
          gaComponent="IconButton"
          handleClick={onDownload}
          icon={{ category: 'global', name: 'file' }}
          size="small"
          theme={theme}
        />
      ) : (
        <Spec variant="gray" theme="light" size="extraSmall" type="sans" title="-" />
      )}
      {downloadErrorMessage ? (
        <Box mt={8}>
          <Typography
            variant="body"
            size="small"
            color="error-1"
            theme={theme}
            data-testid="financing-agreement-download-error"
          >
            {downloadErrorMessage}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

/** Stable custom components configuration to prevent unnecessary re-renders */
const customComponents = {
  BusinessNameSearch: BusinessNameSearch as React.ComponentType<Record<string, unknown>>,
  FinancingAgreementField: FinancingAgreementField as React.ComponentType<Record<string, unknown>>,
};

type FinanceProviderDetailsProps = {
  theme: 'light' | 'dark';
  data?: FinanceProviderDetailsData;
  labels: Record<string, string>;
  onSave?: (data: FinanceProviderDetailsData) => void;
  dataTestId?: string;
  moduleFileReferenceDtoList?: ModuleFileReference[];
  sfdcDeliveryIdentifier?: string | null;
};

/**
 * FinanceProviderDetails component displays and manages finance provider information.
 * Renders a form with finance provider details and financing agreement document viewer.
 * Supports document download via FileHub integration with XSS-safe link rendering.
 *
 * @component
 * @param {Object} props - Component props
 * @param {'light' | 'dark'} props.theme - Theme for the component (light or dark)
 * @param {FinanceProviderDetailsData} [props.data] - Finance provider data to display
 * @param {Record<string, string>} props.labels - i18n labels for form fields and messages
 * @param {Function} [props.onSave] - Callback fired when form is saved with updated data
 * @param {string} [props.dataTestId='finance-provider-details'] - Test ID for the root element
 * @param {Array} [props.moduleFileReferenceDtoList] - List of file references including financing agreement documents
 * @returns {JSX.Element} Rendered FinanceProviderDetails component
 *
 * @example
 * ```tsx
 * <FinanceProviderDetails
 *   theme="dark"
 *   data={financeProviderData}
 *   labels={labels}
 *   onSave={handleSave}
 *   dataTestId="finance-provider-details"
 *   moduleFileReferenceDtoList={orderData.moduleFileReferenceDtoList}
 * />
 * ```
 */
const FinanceProviderDetails: React.FC<FinanceProviderDetailsProps> = ({
  theme,
  data,
  labels,
  onSave,
  dataTestId = 'finance-provider-details',
  moduleFileReferenceDtoList,
  sfdcDeliveryIdentifier,
}) => {
  const formRef = useRef<FormRef>(null);
  const [downloadErrorMessage, setDownloadErrorMessage] = useState<string | null>(null);
  const [isDownloadingAgreement, setIsDownloadingAgreement] = useState(false);

  // Memoize default values to prevent unnecessary re-renders
  const defaultValues = useMemo(
    () => ({
      name: data?.name,
      address: data?.address,
      city: data?.city,
      postalCode: data?.postalCode,
      vat: data?.vat,
    }),
    [data?.name, data?.address, data?.city, data?.postalCode, data?.vat]
  );

  const setFormValue = useCallback((name: string, value: unknown) => {
    formRef.current?.setValue(name, value, { shouldValidate: true, shouldDirty: true });
  }, []);

  const handleFinanceProviderChange = useCallback(
    (business: BusinessSearchResult | null) => {
      if (business) {
        // Batch form updates
        setFormValue('name', business.name);
        setFormValue('address', business.address);
        setFormValue('city', business.city);
        setFormValue('postalCode', business.postalCode);
        // Note: VAT number is not provided by D&B API, so it remains empty
      }
    },
    [setFormValue]
  );

  // Find the Finance Agreement document from the file reference list
  const financingAgreementDoc = useMemo((): ModuleFileReference | undefined => {
    const financeAgreementFiles = getFilesByCategory(
      moduleFileReferenceDtoList,
      DOCUMENT_CATEGORY.FINANCING_AGREEMENT
    );
    return financeAgreementFiles[0] ?? undefined;
  }, [moduleFileReferenceDtoList]);

  const financingAgreementDownloadErrorLabel =
    labels?.['form.financingAgreementDownloadError'] ?? 'Unable to fetch finance agreement.';
  const financingAgreementFileId =
    financingAgreementDoc?.filehubIdentifier ?? financingAgreementDoc?.fileIdentifier;
  const financingAgreementSfdcDeliveryId =
    financingAgreementDoc?.sfdcDeliveryIdentifier ?? sfdcDeliveryIdentifier;
  const financingAgreementSfdcFileId = financingAgreementDoc?.sfdcFileIdentifier;

  const getOpenableAgreementBlob = useCallback(
    (blob: Blob): Blob => {
      const isPdfByName = (financingAgreementDoc?.fileNameOriginal || '')
        .toLowerCase()
        .endsWith('.pdf');
      const isPdfByMime = blob.type.toLowerCase().includes('pdf');

      return isPdfByName || isPdfByMime ? blob.slice(0, blob.size, 'application/pdf') : blob;
    },
    [financingAgreementDoc?.fileNameOriginal]
  );

  const openAgreementInNewTab = useCallback(
    (blob: Blob) => {
      const fallbackFileName = 'financing-agreement.pdf';
      const resolvedFileName = financingAgreementDoc?.fileNameOriginal || fallbackFileName;
      const fileForPreview = new File([blob], resolvedFileName, { type: blob.type });
      const blobUrl = globalThis.URL.createObjectURL(fileForPreview);

      window.open(blobUrl, '_blank');

      // Keep URL alive long enough for the new tab to load before cleanup.
      setTimeout(() => {
        globalThis.URL.revokeObjectURL(blobUrl);
      }, 60000);
    },
    [financingAgreementDoc?.fileNameOriginal]
  );

  /**
   * Downloads a financing agreement document and opens it in a new tab
   */
  const handleDownloadAgreement = useCallback(async () => {
    if (!financingAgreementSfdcDeliveryId && !financingAgreementFileId) {
      return;
    }

    setDownloadErrorMessage(null);
    setIsDownloadingAgreement(true);

    try {
      if (financingAgreementSfdcDeliveryId && financingAgreementSfdcFileId) {
        const { blob } = await fetchSfdcFileBlob(
          financingAgreementSfdcDeliveryId,
          financingAgreementSfdcFileId
        );
        const normalizedBlob = getOpenableAgreementBlob(blob);
        openAgreementInNewTab(normalizedBlob);
        return;
      }

      if (!financingAgreementFileId) {
        setDownloadErrorMessage(financingAgreementDownloadErrorLabel);
        return;
      }

      const response = await FileHubApi.instance.get<string>(
        `sales/X/v1/files/${financingAgreementFileId}/download-url`,
        { params: { module_name: FILEHUB_MODULE_NAME.ORDER_MANAGEMENT } }
      );
      const downloadUrl = response.data;

      if (downloadUrl) {
        const fileResponse = await fetch(downloadUrl);
        if (!fileResponse.ok) {
          throw new Error(`Failed to fetch file: ${fileResponse.status}`);
        }
        const blob = await fileResponse.blob();
        const normalizedBlob = getOpenableAgreementBlob(blob);
        openAgreementInNewTab(normalizedBlob);
        return;
      }

      setDownloadErrorMessage(financingAgreementDownloadErrorLabel);
    } catch (error) {
      setDownloadErrorMessage(financingAgreementDownloadErrorLabel);
      console.error('Failed to download financing agreement:', error);
    } finally {
      setIsDownloadingAgreement(false);
    }
  }, [
    financingAgreementDownloadErrorLabel,
    financingAgreementFileId,
    financingAgreementSfdcDeliveryId,
    financingAgreementSfdcFileId,
    getOpenableAgreementBlob,
    openAgreementInNewTab,
  ]);

  const financeProviderFormFields = useMemo(
    () =>
      createFinanceProviderFormFields(labels, defaultValues, theme, handleFinanceProviderChange, {
        hasAgreement: !!financingAgreementDoc,
        onDownload: handleDownloadAgreement,
        downloadErrorMessage,
      }),
    [
      labels,
      defaultValues,
      theme,
      handleFinanceProviderChange,
      financingAgreementDoc,
      handleDownloadAgreement,
      downloadErrorMessage,
    ]
  );

  const handleSuccess = useCallback(
    (formData: unknown) => {
      const typedData = formData as FinanceProviderDetailsData;
      onSave?.(typedData);
    },
    [onSave]
  );

  return (
    <Box data-testid={dataTestId}>
      {isDownloadingAgreement && (
        <Box className="fetchingOverlay" data-testid="financing-agreement-loading-overlay">
          <Loader size={48} theme={theme === 'dark' ? 'light' : 'dark'} />
        </Box>
      )}

      {/* Finance Provider Form */}
      <Form
        id="finance-provider-form"
        ref={formRef}
        theme={theme === 'dark' ? 'light' : 'dark'}
        formFields={financeProviderFormFields}
        onSuccess={handleSuccess}
        onError={() => {}}
        isFluid={true}
        customComponents={customComponents}
      />
    </Box>
  );
};

FinanceProviderDetails.displayName = 'FinanceProviderDetails';

export default FinanceProviderDetails;
